"use client";

import Link from "next/link";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { useBookingModal } from "@/contexts/BookingModalContext";
import { routes } from "@/lib/routes";
import { NEW_IMAGES } from "@/lib/images";

const WA_NUMBER = "5492944636295";

function whatsappHref(activityLabel: string) {
  const text = encodeURIComponent(activityLabel);
  return `https://wa.me/${WA_NUMBER}?text=${text}`;
}

export default function OtherSportsPage() {
  const { t } = useTranslation();
  const { openModal } = useBookingModal();

  const activities = [
    { key: "basketball" as const, image: NEW_IMAGES.otherSports.basketball },
    { key: "newcom" as const, image: NEW_IMAGES.otherSports.newcom },
    { key: "volleyball" as const, image: NEW_IMAGES.otherSports.volleyball },
    { key: "training" as const, image: NEW_IMAGES.otherSports.training },
    { key: "camps" as const, image: NEW_IMAGES.otherSports.camps },
  ] as const;

  return (
    <div className="min-h-screen bg-surface-muted">
      {/* Hero */}
      <section className="relative flex min-h-[min(52vh,520px)] flex-col items-center justify-center overflow-hidden px-4 pb-16 pt-24 sm:min-h-[340px] md:min-h-[380px]">
        <div className="absolute inset-0 bg-slate-950" aria-hidden />
        <div
          className="absolute inset-0 opacity-[0.5]"
          style={{
            backgroundImage: "url('/new/1.jpg')",
            backgroundPosition: "center center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_90%_70%_at_50%_15%,transparent_0%,rgba(15,23,42,0.88)_55%,rgb(15,23,42)_100%)]"
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-linear-to-b from-slate-950/20 via-slate-950/65 to-slate-950"
          aria-hidden
        />

        <div className="relative z-10 mx-auto w-full max-w-3xl px-2 text-center">
          <p className="mx-auto mb-6 inline-flex max-w-lg items-center justify-center rounded-full border border-white/20 bg-white/8 px-5 py-2.5 text-[13px] font-medium leading-relaxed text-white/95 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] backdrop-blur-md sm:max-w-2xl sm:text-[0.9375rem] sm:leading-snug">
            {t("otherSports.subtitle")}
          </p>
          <h1 className="text-[1.9rem] font-semibold tracking-[-0.035em] text-white sm:text-4xl md:text-[2.75rem] md:leading-[1.1]">
            {t("otherSports.title")}
          </h1>
          <nav
            className="mt-6 flex items-center justify-center gap-2 text-[13px] font-medium text-slate-400"
            aria-label="Breadcrumb"
          >
            <Link href={routes.home} className="transition-colors hover:text-white">
              {t("header.home")}
            </Link>
            <span className="text-slate-600" aria-hidden>
              —
            </span>
            <span className="text-slate-200">{t("otherSports.title")}</span>
          </nav>

          <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:mt-10 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={openModal}
              className="inline-flex min-h-[48px] items-center justify-center rounded-full bg-primary px-7 py-3 text-sm font-semibold text-white shadow-[0_8px_28px_-6px_rgba(34,72,214,0.55)] transition hover:bg-primary-strong active:scale-[0.98]"
            >
              {t("header.bookSlot")}
            </button>
            <a
              href={whatsappHref(
                t("otherSports.waInquiry", { activity: t("otherSports.title") }),
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-white/25 bg-white/10 px-7 py-3 text-sm font-semibold text-white backdrop-blur-sm transition hover:border-white/40 hover:bg-white/15"
            >
              <MessageCircle className="h-4 w-4 opacity-90" aria-hidden />
              {t("otherSports.inquiryCta")}
            </a>
          </div>
        </div>
      </section>

      {/* Activities */}
      <section className="relative z-10 -mt-6 border-t border-slate-200/80 bg-surface-muted pb-20 pt-14 sm:-mt-8 sm:pt-16 sm:pb-28">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
          <header className="mx-auto mb-12 max-w-2xl text-center sm:mb-16">
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-primary sm:text-xs">
              {t("otherSports.sectionTitle")}
            </p>
            <h2 className="mt-8 text-pretty text-xl font-normal leading-snug tracking-tight text-slate-800 sm:mt-10 sm:text-2xl sm:leading-[1.35] md:text-[1.65rem]">
              {t("otherSports.sectionLead")}
            </h2>
          </header>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-x-7 sm:gap-y-10 lg:grid-cols-3">
            {activities.map(({ key, image }) => {
              const label = t(`otherSports.${key}`);
              const blurb = t(`otherSports.${key}Blurb`);
              const waLink = whatsappHref(
                t("otherSports.waInquiry", { activity: label }),
              );
              return (
                <a
                  key={key}
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-[0_4px_6px_-1px_rgba(15,23,42,0.06),0_12px_32px_-8px_rgba(15,23,42,0.12)] ring-1 ring-slate-200/90 transition duration-300 hover:-translate-y-1 hover:shadow-[0_8px_16px_-4px_rgba(15,23,42,0.08),0_24px_40px_-12px_rgba(34,72,214,0.14)] hover:ring-primary/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  <div className="relative aspect-5/3 w-full overflow-hidden bg-slate-100">
                    <Image
                      src={image}
                      alt={`${label} — ${t("common.venueName")}`}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      className="object-cover object-center transition duration-500 ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="flex flex-1 flex-col border-t border-slate-100 px-6 pb-6 pt-5">
                    <h3 className="text-lg font-semibold leading-snug tracking-tight text-slate-900">
                      {label}
                    </h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{blurb}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition group-hover:gap-2">
                      {t("otherSports.cardInquire")}
                      <ArrowUpRight className="h-4 w-4 shrink-0" aria-hidden />
                    </span>
                  </div>
                </a>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="mt-14 overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_8px_40px_-12px_rgba(15,23,42,0.12)] sm:mt-20">
            <div className="grid gap-0 md:grid-cols-[1fr_auto] md:items-center">
              <div className="border-b border-slate-100 bg-linear-to-br from-slate-50 to-white p-8 sm:p-10 md:border-b-0 md:border-r">
                <h3 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                  {t("otherSports.ctaHeadline")}
                </h3>
                <p className="mt-3 max-w-lg text-pretty text-slate-600 leading-relaxed">
                  {t("otherSports.ctaDesc")}
                </p>
              </div>
              <div className="flex flex-col justify-center gap-4 p-8 sm:p-10">
                <a
                  href={whatsappHref(
                    t("otherSports.waInquiry", { activity: t("otherSports.title") }),
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-[52px] items-center justify-center gap-2.5 rounded-xl bg-[#25D366] px-8 py-4 text-base font-bold text-white shadow-lg transition hover:brightness-110 active:scale-[0.98]"
                >
                  <WhatsAppIcon size={22} className="h-5 w-5 shrink-0" />
                  {t("otherSports.inquiryCta")}
                </a>
                <button
                  type="button"
                  onClick={openModal}
                  className="inline-flex min-h-[48px] items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                >
                  {t("header.bookSlot")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
